/**
 * negativeBalanceBanner — pure, import-free decision + copy for the header "credits have run out" banner.
 *
 * Kept import-free so cypress/node-tests/negative-balance-banner.test.js executes THIS file (not a mirror).
 *
 * Data contract (backend-written, myaccount-service/balance-publish.js): siteWallet/{siteID} carries
 *   negativeBalanceAlert: { sms, email }   true when that channel is below $0 AND nothing will refill it
 *                                           (auto-reload off / failed / monthly limit) — server-authoritative,
 *                                           so a healthy auto-reloading site dipping below $0 for a second never shows it
 *   graceUsd: { sms, email }               how far below zero sends continue before they stop
 *   channels.{sms,email}.paidBalanceUsd    the (negative) balance
 *   channels.{sms,email}.creditPriceUsd    price of one credit (sizes the "how many more messages" estimate)
 *
 * A channel shows only when its flag is on AND its balance is actually below zero: right after a top-up the
 * new balance lands a moment before the flag clears, and the banner must never read "$0.00 below zero".
 *
 * The message estimate is the SAME math as the practice email (myaccount-service/low-balance-alerts.js
 * messagesLeftEstimate) — both tests pin the same vectors (-2.40 → 34 to 104 texts).
 */

const DEFAULT_GRACE_USD = 5;
const DEFAULT_PRICE_USD = { sms: 0.025, email: 0.005 };
const PHOTO_TEXT_CREDITS = 3; // a text with a photo = 3 credits (layer smsSegments.MMS_CREDITS)

const CHANNEL_COPY = {
  sms: { label: 'SMS', noun: 'Texts', one: 'text', verb: 'texting', examples: 'appointment reminders and replies',
    caveat: 'an estimate — longer texts, photos, and patient replies use more' },
  email: { label: 'email', noun: 'Emails', one: 'email', verb: 'emailing', examples: 'confirmations, recall emails, and forms',
    caveat: 'an estimate' },
};

// Dollars below zero rounded UP to the cent (same rule as the email), e.g. -0.005 → "0.01".
export function centsBelowZero(balanceUsd) {
  const owed = Math.max(0, -Number(balanceUsd) || 0);
  return (Math.ceil(owed * 100 - 1e-9) / 100).toFixed(2);
}

function balanceFor(wallet, product) {
  const ch = wallet && wallet.channels && wallet.channels[product];
  if (ch && ch.paidBalanceUsd != null) return Number(ch.paidBalanceUsd);
  if (product === 'sms' && wallet && wallet.paidBalanceUsd != null) return Number(wallet.paidBalanceUsd);
  return NaN;
}

function graceFor(wallet, product) {
  const g = Number(wallet && wallet.graceUsd && wallet.graceUsd[product]);
  return Number.isFinite(g) && g > 0 ? g : DEFAULT_GRACE_USD;
}

function priceFor(wallet, product) {
  const ch = wallet && wallet.channels && wallet.channels[product];
  const p = Number(ch && ch.creditPriceUsd);
  return Number.isFinite(p) && p > 0 ? p : DEFAULT_PRICE_USD[product];
}

/**
 * How many more messages fit before sends stop: credits = (balance + stop point) / price. Texts are a
 * RANGE (all short texts at 1 credit … all photo texts at 3); emails are one credit each.
 */
export function messagesLeftEstimate({ product = 'sms', balanceUsd, graceUsd = DEFAULT_GRACE_USD, priceUsd } = {}) {
  const price = Number(priceUsd) > 0 ? Number(priceUsd) : (DEFAULT_PRICE_USD[product] || DEFAULT_PRICE_USD.sms);
  const headroomUsd = (Number(balanceUsd) || 0) + (Number(graceUsd) || 0);
  const credits = Math.max(0, Math.floor(headroomUsd / price + 1e-9));
  if (credits <= 0) return { credits: 0, low: 0, high: 0, stopped: true };
  if (product === 'email') return { credits, low: credits, high: credits, stopped: false };
  return { credits, low: Math.floor(credits / PHOTO_TEXT_CREDITS), high: credits, stopped: false };
}

// "roughly 34 to 104 more texts" / "up to 2 more texts" / "about 800 more emails".
export function messagesLeftPhrase(est, product) {
  const one = (CHANNEL_COPY[product] || CHANNEL_COPY.sms).one;
  const many = `${one}s`;
  const fmt = (n) => Number(n).toLocaleString('en-US');
  if (est.low === est.high) return `about ${fmt(est.high)} more ${est.high === 1 ? one : many}`;
  if (est.low < 1) return `up to ${fmt(est.high)} more ${est.high === 1 ? one : many}`;
  return `roughly ${fmt(est.low)} to ${fmt(est.high)} more ${many}`;
}

function estimateFor(wallet, product) {
  return messagesLeftEstimate({
    product, balanceUsd: balanceFor(wallet, product), graceUsd: graceFor(wallet, product), priceUsd: priceFor(wallet, product),
  });
}

/** Which channels are below zero right now → { sms, email, any }. */
export function negativeChannels(wallet) {
  const flags = (wallet && wallet.negativeBalanceAlert) || {};
  const on = (product) => !!flags[product] && balanceFor(wallet, product) < -1e-9;
  const sms = on('sms');
  const email = on('email');
  return { sms, email, any: sms || email };
}

/**
 * The low-balance flags with every below-zero channel removed — the "run out" banner supersedes the
 * "running low" one for that channel, so the two never say different things about the same pool.
 */
export function effectiveLowBalance(lowFlags, negative) {
  const low = lowFlags || {};
  const neg = negative || {};
  const sms = !!low.sms && !neg.sms;
  const email = !!low.email && !neg.email;
  return { sms, email, any: sms || email };
}

// One channel's "how long have I got" sentence: "You can send roughly 34 to 104 more texts (an estimate —
// …) before texts stop, including …" or, at the stop point, "Texts have stopped, including …".
function channelSentence(wallet, product, examples) {
  const c = CHANNEL_COPY[product];
  const est = estimateFor(wallet, product);
  const incl = examples ? `, including ${examples}` : '';
  if (est.stopped) return { stopped: true, text: `${c.noun} have stopped${incl}.` };
  return {
    stopped: false,
    text: `You can send ${messagesLeftPhrase(est, product)} (${c.caveat}) before ${c.noun.toLowerCase()} stop${incl}.`,
  };
}

/** Banner copy { title, text } for the below-zero channels, or null when none is below zero. */
export function negativeBannerCopy(wallet) {
  const neg = negativeChannels(wallet);
  if (!neg.any) return null;

  if (neg.sms && neg.email) {
    const s = centsBelowZero(balanceFor(wallet, 'sms'));
    const e = centsBelowZero(balanceFor(wallet, 'email'));
    const ss = channelSentence(wallet, 'sms', CHANNEL_COPY.sms.examples);
    const es = channelSentence(wallet, 'email', CHANNEL_COPY.email.examples);
    const allStopped = ss.stopped && es.stopped;
    return {
      title: 'Your SMS and email credits have run out',
      text: `Your SMS balance is $${s} below zero and your email balance is $${e} below zero. ${ss.text} ${es.text} ` +
        `Add credits or check your auto-reload settings to ${allStopped ? 'start reaching patients again' : 'keep reaching patients'}.`,
    };
  }

  const product = neg.sms ? 'sms' : 'email';
  const c = CHANNEL_COPY[product];
  const owed = centsBelowZero(balanceFor(wallet, product));
  const sentence = channelSentence(wallet, product, c.examples);
  return {
    title: `Your ${c.label} credits have run out`,
    text: `Your balance is $${owed} below zero. ${sentence.text} ` +
      `Add credits or check your auto-reload settings to ${sentence.stopped ? `start ${c.verb} patients again` : `keep ${c.verb} patients`}.`,
  };
}
